import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, CalendarOff, Check, Clock, X } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton } from "../components/ui";
import { SelfPunchCards } from "../components/SelfPunchCard";
import { formatDate } from "../lib/format";
import { ATTENDANCE_LABEL, ATTENDANCE_TONE, formatMinutes } from "../lib/people";
import type { EmployeeFull, LeaveRequest } from "../api/types";

/**
 * The Manager desk: the team today, and the decisions only this manager can
 * make. It reads the reporting subtree the server already scopes -- a manager
 * sees their own reports and never another manager's team, and never company
 * payroll or salary.
 */
export function ManagerDeskPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;
  const today = useMemo(() => localDate(timezone), [timezone]);
  const queryClient = useQueryClient();

  const me = useQuery({
    queryKey: ["self-employee"],
    queryFn: ({ signal }) => api.employeeMe(signal),
  });

  const team = useQuery({
    queryKey: ["manager-desk-team", me.data?.id],
    queryFn: ({ signal }) =>
      api.employeeList({ reporting_to: me.data!.id, page_size: 100 }, signal),
    enabled: Boolean(me.data?.id),
  });

  const attendance = useQuery({
    queryKey: ["manager-desk-attendance", today],
    queryFn: ({ signal }) => api.attendancePage({ date: today, page_size: 100 }, signal),
  });
  const shifts = useQuery({
    queryKey: ["manager-desk-shifts", today],
    queryFn: ({ signal }) => api.shiftsPage({ from: today, to: today, page_size: 200 }, signal),
  });
  const pendingLeave = useQuery({
    queryKey: ["manager-desk-leave"],
    queryFn: ({ signal }) => api.leaveRequests({ scope: "team", status: "pending", page_size: 20 }, signal),
  });
  const tasks = useQuery({
    queryKey: ["manager-desk-tasks"],
    queryFn: ({ signal }) => api.tasksMine(signal),
  });

  const invalidateLeave = () => {
    void queryClient.invalidateQueries({ queryKey: ["manager-desk-leave"] });
  };
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" }) =>
      action === "approve" ? api.approveLeaveRequest(id) : api.rejectLeaveRequest(id),
    onSuccess: invalidateLeave,
  });

  // A manager whose login has no employment record has no reporting subtree to
  // manage. Surface it rather than rendering an empty team as if it were fine.
  if (me.isError) {
    return (
      <>
        <PageHeader title="My Desk" subtitle="Your team today." />
        <EmptyState
          title="No employee record linked to this login"
          body="A reporting subtree needs an employment record. Ask an owner to link your login to your employee record under Employees."
        />
      </>
    );
  }

  const teamRows = (team.data?.results ?? []).filter((p) => p.status !== "exited");
  const teamIds = new Set(teamRows.map((p) => p.id));
  const teamShifts = (shifts.data?.results ?? []).filter(
    (s) => teamIds.has(s.employee) && !s.is_off,
  );
  const teamAttendance = (attendance.data?.results ?? []).filter((row) =>
    teamIds.has(row.employee),
  );
  const present = teamAttendance.filter(
    (row) => row.status === "present" || row.status === "half_day",
  );
  const missingCheckout = teamAttendance.filter(
    (row) => row.clock_in_at && !row.clock_out_at && row.status !== "on_leave",
  );
  const scheduledIds = new Set(teamShifts.map((s) => s.employee));
  const checkedIds = new Set(present.map((row) => row.employee));
  const notIn = [...scheduledIds].filter((id) => !checkedIds.has(id));

  const loading = me.isPending || team.isPending || attendance.isPending || shifts.isPending;

  return (
    <>
      <PageHeader
        title="My Desk"
        subtitle={`Your team on ${formatDate(today, timezone)}.`}
        actions={
          <Link
            to="/people/leave"
            className="inline-flex items-center gap-1 text-sm font-medium text-slate-700 hover:underline"
          >
            Team leave <ArrowRight size={13} />
          </Link>
        }
      />

      <div className="mb-6">
        <SelfPunchCards />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Tile icon={<Clock size={15} />} label="Team size" value={teamRows.length} loading={loading} to="/people/employees" />
        <Tile icon={<CalendarOff size={15} />} label="Scheduled today" value={teamShifts.length} loading={loading} to="/people/roster" />
        <Tile icon={<Check size={15} />} label="Present" value={present.length} loading={loading} to="/people/attendance" />
        <Tile
          icon={<AlertTriangle size={15} />}
          label="Needs follow-up"
          value={notIn.length + missingCheckout.length}
          loading={loading}
          to="/people/attendance"
          warn={notIn.length + missingCheckout.length > 0}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Team leave to decide
            <Link to="/people/leave?scope=team" className="text-xs font-normal text-slate-500 hover:text-slate-700">
              All →
            </Link>
          </h2>
          {pendingLeave.isPending ? (
            <Skeleton className="h-20 w-full" />
          ) : (pendingLeave.data?.results ?? []).length === 0 ? (
            <EmptyState title="Nothing waiting" body="No leave requests from your direct reports." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {(pendingLeave.data?.results ?? []).slice(0, 6).map((leave: LeaveRequest) => (
                <li key={leave.id} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">{leave.employee_name}</p>
                    <p className="text-xs text-slate-500">
                      {leave.leave_type_name} · {formatDate(leave.from_date)} → {formatDate(leave.to_date)} ·{" "}
                      {leave.days}d
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {leave.can_decide ? (
                      <>
                        <Button
                          variant="ghost"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: leave.id, action: "approve" })}
                        >
                          <Check size={14} /> Approve
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: leave.id, action: "reject" })}
                        >
                          <X size={14} /> Reject
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-400">
                        {leave.approver_name ? `With ${leave.approver_name}` : "With the owner"}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {decide.error instanceof ApiError ? (
            <p role="alert" className="mt-2 text-sm text-rose-600">
              {decide.error.message}
            </p>
          ) : null}
        </Card>

        <Card>
          <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
            Who is in today
            <Link to="/people/attendance" className="text-xs font-normal text-slate-500 hover:text-slate-700">
              Register →
            </Link>
          </h2>
          {!loading && teamRows.length > 0 ? (
            <p className="mb-2 text-xs text-slate-500">
              {present.length} of {teamShifts.length} scheduled reported in
              {notIn.length ? ` · ${notIn.length} not reported` : ""}
            </p>
          ) : null}
          {loading ? (
            <Skeleton className="h-24 w-full" />
          ) : teamRows.length === 0 ? (
            <EmptyState title="No direct reports" body="Nobody reports to you yet. Set Reports To under Employees." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {teamRows.slice(0, 8).map((person: EmployeeFull) => {
                const row = teamAttendance.find((a) => a.employee === person.id);
                const status = row?.status;
                const scheduled = scheduledIds.has(person.id);
                return (
                  <li key={person.id} className="flex items-center justify-between py-2">
                    <Link to={`/people/employees/${person.id}`} className="text-sm text-slate-800 hover:underline">
                      {person.display_name}
                    </Link>
                    {status ? (
                      <Badge tone={ATTENDANCE_TONE[status] ?? "slate"}>
                        {ATTENDANCE_LABEL[status] ?? status}
                      </Badge>
                    ) : scheduled ? (
                      <Badge tone="amber">Not checked in</Badge>
                    ) : (
                      <span className="text-xs text-slate-400">No shift</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>

      <Card className="mt-6">
        <h2 className="mb-3 flex items-center justify-between text-sm font-semibold text-slate-900">
          My tasks
          <Link to="/people/desk" className="text-xs font-normal text-slate-500 hover:text-slate-700">
            Desk →
          </Link>
        </h2>
        {tasks.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (tasks.data ?? []).length === 0 ? (
          <p className="text-sm text-slate-500">No open tasks assigned to you.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {(tasks.data ?? []).slice(0, 6).map((task) => (
              <li key={task.id} className="flex items-center justify-between py-2">
                <span className="text-sm text-slate-800">{task.subject || "Task"}</span>
                {task.due_at ? (
                  <span className="text-xs text-slate-500">Due {formatDate(task.due_at)}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {missingCheckout.length ? (
        <Card className="mt-6">
          <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900">
            <AlertTriangle size={15} className="text-amber-500" /> Missing check-outs today
          </h2>
          <ul className="divide-y divide-slate-100">
            {missingCheckout.map((row) => (
              <li key={row.id} className="flex items-center justify-between py-2">
                <Link to={`/people/employees/${row.employee}`} className="text-sm text-slate-800 hover:underline">
                  {row.employee_name}
                </Link>
                <span className="text-xs text-slate-500">
                  In {formatMinutes(row.worked_minutes)} · no check-out
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-slate-400">
            Approve a correction from the employee&apos;s profile once they file one.
          </p>
        </Card>
      ) : null}
    </>
  );
}

function Tile({
  icon,
  label,
  value,
  loading,
  to,
  warn,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  loading: boolean;
  to: string;
  warn?: boolean;
}) {
  return (
    <Link
      to={to}
      className="group block rounded-lg border border-slate-200 bg-white p-4 shadow-xs transition hover:border-slate-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2"
    >
      <div className="flex items-center gap-2 text-slate-500">
        {icon}
        <span className="block min-h-8 text-xs font-medium uppercase leading-tight tracking-wide">
          {label}
        </span>
      </div>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-12" />
      ) : (
        <p className={`mt-1 text-2xl font-semibold tabular-nums ${warn ? "text-amber-600" : "text-slate-900"}`}>
          {value}
        </p>
      )}
    </Link>
  );
}

function localDate(timeZone?: string | null): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timeZone ?? undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const map: Record<string, string> = {};
  for (const part of parts) map[part.type] = part.value;
  return `${map.year}-${map.month}-${map.day}`;
}
